import { Autocomplete as BaseAutocomplete } from '@base-ui-components/react/autocomplete';
import Icon, { IconName } from '@features/ui-atoms/components/Icon';
import Spinner from '@features/ui-atoms/components/Spinner';
import Tag from '@features/ui-atoms/components/Tag';
import { useAutocompleteFocus } from '@features/ui-form/hooks/useAutocompleteFocus';
import { useAutocompleteScroll } from '@features/ui-form/hooks/useAutocompleteScroll';
import { ResetButton } from './Select';
import { safeJsonParse } from '@root/src/utils/safeParse';
import { styled } from '@mui/joy';
import { useTranslation } from 'next-i18next';
import _ from 'lodash';
import React, { ReactNode, Key, useCallback, useEffect, useRef, useMemo, useState } from 'react';

type SelectOption<OptionValueType> = {
  label: ReactNode;
  labelString?: string;
  searchString?: string;
  value: OptionValueType;
};

type Jsonified<_T> = string;
type MaybeMultiple<T, Multiple> = Multiple extends true ? Array<T> : T;
type WithCustom<T, AllowCustomValue> = AllowCustomValue extends true ? T | string : T;

type AutocompleteProps<OptionValueType, Multiple extends boolean, AllowCustomValue extends boolean> = {
  // Value management
  value?: Jsonified<MaybeMultiple<WithCustom<OptionValueType, AllowCustomValue>, Multiple>>;
  onChange?: (event: {
    target: { value: Jsonified<MaybeMultiple<WithCustom<OptionValueType, AllowCustomValue>, Multiple>> };
  }) => void;
  options: Array<SelectOption<OptionValueType>>;
  disableSerialization?: boolean;

  // Basic props
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;

  // Decorators
  startDecorator?: ReactNode;
  endDecorator?: ReactNode;
  iconColor?: string;

  // Search & Filter
  onSearch?: (search: string) => void;
  inputValue?: string;
  filterSelectedOptions?: boolean;

  // Infinite scroll
  enableFetchOnScroll?: boolean;
  onListboxScroll?: (event: React.UIEvent<HTMLDivElement>) => void;

  // Loading & State
  loading?: boolean;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;

  // Dropdown behavior
  closeOnSelection?: boolean;

  // Select all / Deselect all toggle (only for multiple mode with static options)
  enableSelectAll?: boolean;

  // Styling overrides for the input container only
  inputContainerStyle?: React.CSSProperties;
};

type AutocompleteComponentProps<OptionValueType> = AutocompleteProps<OptionValueType, boolean, boolean> & {
  multiple: boolean;
  allowCustomValue: boolean;
  enableSelectAll?: boolean;
};

const ClearSelectionButton = styled(ResetButton)`
  padding-inline: 0;
  color: ${({ theme }) => theme.vars.palette.grey4};

  &:hover {
    color: ${({ theme }) => theme.vars.palette.bluePrimary};
  }
`;

/**
 * Clean Autocomplete Component built from scratch with MUI Base
 *
 * Features:
 * 1. Single & Multiple selection modes
 * 2. Custom values support (allowCustomValue)
 * 3. Value serialization/deserialization (JSON or direct)
 * 4. Controlled input value
 * 5. Search/Filter with onSearch callback
 * 6. Infinite scroll with onListboxScroll
 * 7. Loading state
 * 8. Controlled open/close
 * 9. Placeholder, disabled, error states
 * 10. Start/End decorators
 * 11. Clear/Reset button
 * 12. Arrow toggle button
 * 13. Scroll position restoration
 * 14. Focus management
 * 15. Tag display for multiple selection
 * 16. Checkbox icons for multiple selection
 * 17. Item filtering
 * 18. Empty state
 */
const AutocompleteComponent = <OptionValueType,>({
  value: valueProp,
  onChange,
  options,
  disableSerialization = false,
  placeholder,
  disabled,
  error,
  startDecorator,
  endDecorator,
  iconColor,
  onSearch,
  inputValue: inputValueProp,
  enableFetchOnScroll = false,
  onListboxScroll,
  loading,
  open,
  onOpen,
  onClose,
  closeOnSelection = true,
  enableSelectAll = false,
  inputContainerStyle,
  multiple,
  allowCustomValue,
}: AutocompleteComponentProps<OptionValueType>) => {
  const { t } = useTranslation('common');

  // The border of the input container at rest: orange in error, as Input and Select draw it.
  const restingBorderColor = error ? 'var(--joy-palette-orange1)' : 'transparent';

  // Support both controlled and uncontrolled open state.
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const isControlledOpen = open !== undefined;
  const effectiveOpen = isControlledOpen ? !!open : internalOpen;

  // ===== HOOKS =====
  const { setupScrollListener, listboxRef, restoreScrollPosition } = useAutocompleteScroll(
    enableFetchOnScroll ? onListboxScroll : undefined,
    loading,
    options,
    open,
  );

  const {
    inputRef,
    rootRef,
    handleInputChange: handleInputChangeFromHook,
    handleFocus,
    handleBlur,
    handleOpenInternal: handleOpenFromHook,
    handleRootClick,
  } = useAutocompleteFocus({
    onSearch,
    open: effectiveOpen,
    // When open state is uncontrolled, delegate to internal state setters.
    // Guard with suppressOpenRef to prevent reopening after close-on-selection.
    onOpen: isControlledOpen
      ? () => {
          if (!suppressOpenRef.current) onOpen?.();
        }
      : () => {
          if (!suppressOpenRef.current) setInternalOpen(true);
        },
    onClose: isControlledOpen ? onClose : () => setInternalOpen(false),
    options,
    inputValue: inputValueProp,
    // Always open the dropdown when the input gains focus.
    autoOpenOnFocus: true,
  });

  // ===== STATE =====
  const [internalInputValue, setInternalInputValue] = useState(inputValueProp ?? '');
  const [isClearingInput, setIsClearingInput] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const previousValueRef = useRef(valueProp);
  const inputContainerRef = useRef<HTMLDivElement | null>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number>(300);
  // Ref to suppress BaseAutocomplete onOpenChange(true) after a close-on-selection
  const suppressOpenRef = useRef(false);
  // Cache the last resolved selected option so it survives options list changes
  // (e.g. when clearing a search filter causes a refetch that no longer includes the selected item)
  const selectedOptionCacheRef = useRef<Map<string, SelectOption<OptionValueType>>>(new Map());

  // Sync input value with prop, but don't override while we are actively clearing
  useEffect(() => {
    if (inputValueProp !== undefined && !isClearingInput) {
      setInternalInputValue(inputValueProp);
    }
  }, [inputValueProp, isClearingInput]);

  // Restore scroll position when value changes
  useEffect(() => {
    if (!effectiveOpen) {
      previousValueRef.current = valueProp;
      return;
    }
    if (previousValueRef.current !== valueProp) {
      previousValueRef.current = valueProp;
      restoreScrollPosition();
    }
  }, [effectiveOpen, valueProp, restoreScrollPosition]);

  // Measure input container width for dropdown
  useEffect(() => {
    const updateDropdownWidth = () => {
      if (inputContainerRef.current) {
        const width = inputContainerRef.current.offsetWidth;
        setDropdownWidth(Math.max(width, 300));
      }
    };

    updateDropdownWidth();
    window.addEventListener('resize', updateDropdownWidth);
    return () => window.removeEventListener('resize', updateDropdownWidth);
  }, [effectiveOpen]); // Update when dropdown opens/closes

  // ===== VALUE PARSING =====
  const internalSelectedValue = useMemo(() => {
    if (multiple) {
      if (valueProp === '' || valueProp == null) return [];
      const arrayOfValues = disableSerialization
        ? (valueProp as unknown as Array<OptionValueType>)
        : (safeJsonParse(valueProp) as Array<OptionValueType>);
      const safeArray = Array.isArray(arrayOfValues) ? arrayOfValues : [];
      return safeArray
        .map(v => {
          const found = options.find(so => _.isEqual(so.value, v));
          if (found) {
            selectedOptionCacheRef.current.set(JSON.stringify(v), found);
            return found;
          }
          const cacheKey = JSON.stringify(v);
          const cached = selectedOptionCacheRef.current.get(cacheKey);
          if (cached) return cached;
          if (allowCustomValue) return String(v);
          return null;
        })
        .filter((item): item is SelectOption<OptionValueType> | string => item !== null);
    } else {
      if (valueProp === '' || valueProp == null) {
        selectedOptionCacheRef.current.clear();
        return null;
      }
      const parsed = disableSerialization
        ? (valueProp as unknown as OptionValueType)
        : safeJsonParse<OptionValueType>(valueProp);
      const found = options.find(so => _.isEqual(so.value, parsed));
      if (found) {
        selectedOptionCacheRef.current.set(JSON.stringify(parsed), found);
        return found;
      }
      const cacheKey = JSON.stringify(parsed);
      const cached = selectedOptionCacheRef.current.get(cacheKey);
      if (cached) return cached;
      if (allowCustomValue) return String(parsed);
      return null;
    }
  }, [valueProp, options, multiple, allowCustomValue, disableSerialization]);

  // ===== VALUE CHANGE HANDLER =====
  const handleValueChange = useCallback(
    (newValue: SelectOption<OptionValueType> | string | Array<SelectOption<OptionValueType> | string> | null) => {
      // Cache selected option(s) BEFORE clearing the search, so the cache is populated
      // before the options refetch (triggered by onSearch('')) replaces the options list.
      if (multiple) {
        const array = (newValue as Array<SelectOption<OptionValueType> | string>) ?? [];
        array.forEach(x => {
          if (typeof x !== 'string') {
            selectedOptionCacheRef.current.set(JSON.stringify(x.value), x);
          }
        });
        const serialized = disableSerialization
          ? String(array.map(x => (typeof x === 'string' ? x : x.value)))
          : JSON.stringify(array.map(x => (typeof x === 'string' ? x : x.value)));
        onChange?.({ target: { value: serialized as Jsonified<any> } });
      } else {
        if (newValue == null) {
          selectedOptionCacheRef.current.clear();
          onChange?.({ target: { value: '' as Jsonified<any> } });
        } else {
          const single = newValue as SelectOption<OptionValueType> | string;
          if (typeof single !== 'string') {
            selectedOptionCacheRef.current.set(JSON.stringify(single.value), single);
          }
          const serialized = disableSerialization
            ? String(typeof single === 'string' ? single : single.value)
            : JSON.stringify(typeof single === 'string' ? single : single.value);
          onChange?.({ target: { value: serialized as Jsonified<any> } });
        }
      }

      // After any value change triggered by selection, clear the search input.
      setInternalInputValue('');
      onSearch?.('');
      handleInputChangeFromHook({ target: { value: '' } } as any, '', 'reset');
    },
    [multiple, disableSerialization, onChange, onSearch, handleInputChangeFromHook],
  );

  // ===== ITEM STRING VALUE =====
  const getItemStringValue = useCallback((item: SelectOption<OptionValueType> | string): string => {
    if (typeof item === 'string') return item;
    return (
      item.searchString ??
      (typeof item.label === 'string'
        ? item.label
        : typeof item.label === 'number'
          ? item.label.toString()
          : (item.labelString ?? ''))
    );
  }, []);

  // ===== BLUR: RESOLVE TYPED-BUT-UNCOMMITTED TEXT =====
  // For a single-select field that does not accept custom values, text the user
  // typed but never committed (by clicking an option) would otherwise be dropped
  // silently — while the input keeps displaying it, so the field looks filled
  // when nothing is actually selected and the surrounding form registers no
  // change. On blur we resolve that text to an option only when it is an exact
  // match or uniquely identifies a single option from the start (prefix), and
  // commit it. Anything ambiguous, mid-word, or unmatched is cleared so the
  // input reverts to the real selection and can never misrepresent state.
  const latestInputValueRef = useRef(internalInputValue);
  latestInputValueRef.current = internalInputValue;
  const latestOptionsRef = useRef(options);
  latestOptionsRef.current = options;

  const resolveTypedValueOnBlur = useCallback(() => {
    if (multiple || allowCustomValue) return;
    // Focus still inside the component (e.g. mid option-click) → let that win.
    if (rootRef.current?.contains(document.activeElement)) return;
    const query = latestInputValueRef.current?.trim();
    if (!query) return; // nothing typed, or an option already committed and cleared it
    const lc = query.toLowerCase();
    const opts = latestOptionsRef.current as Array<SelectOption<OptionValueType>>;
    const str = (o: SelectOption<OptionValueType>) => getItemStringValue(o).toLowerCase();
    const exact = opts.find(o => str(o) === lc);
    const prefix = opts.filter(o => str(o).startsWith(lc));
    const match = exact ?? (prefix.length === 1 ? prefix[0] : undefined);
    if (match) {
      handleValueChange(match);
    } else {
      // Discard the uncommitted text so the input reverts to the real selection.
      setInternalInputValue('');
      setResetKey(prev => prev + 1);
    }
  }, [multiple, allowCustomValue, rootRef, getItemStringValue, handleValueChange]);

  // The deferred resolution updates state, so it must not outlive the component
  // (a picker unmounted right after a pick would otherwise set state on a dead tree).
  const blurResolveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (blurResolveTimerRef.current) clearTimeout(blurResolveTimerRef.current);
    },
    [],
  );

  const handleInputBlur = useCallback(
    (event: React.FocusEvent) => {
      handleBlur(event);
      // Defer so that clicking an option commits its own value first (its
      // handleValueChange clears the search, turning this into a no-op).
      if (blurResolveTimerRef.current) clearTimeout(blurResolveTimerRef.current);
      blurResolveTimerRef.current = setTimeout(resolveTypedValueOnBlur, 150);
    },
    [handleBlur, resolveTypedValueOnBlur],
  );

  // ===== INPUT VALUE HANDLER =====
  const handleBaseInputChange = useCallback(
    (value: string) => {
      // When the user is actively typing and the value becomes empty (e.g., backspace on last character),
      // ensure we clear the input immediately to prevent any value restoration
      // Force remount of BaseAutocomplete to clear its internal state, just like the clear button does
      if (value === '' && internalInputValue && internalInputValue.length > 0) {
        setIsClearingInput(true);
        setInternalInputValue('');
        onSearch?.('');
        handleInputChangeFromHook({ target: { value: '' } } as any, '', 'clear');
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        // Force remount of BaseAutocomplete to ensure internal state is wiped
        setResetKey(prev => prev + 1);
        // Reset clearing flag after a brief delay to allow React to update
        setTimeout(() => {
          setIsClearingInput(false);
        }, 0);
        return;
      }
      setInternalInputValue(value);
      onSearch?.(value);
      handleInputChangeFromHook({ target: { value } } as any, value, 'input');
    },
    [onSearch, handleInputChangeFromHook, internalInputValue, inputRef],
  );

  // ===== DISPLAY VALUE =====
  const displayValue = useMemo(() => {
    // When clearing input, always return empty string regardless of selected value
    if (isClearingInput) return '';
    if (multiple) return internalInputValue;
    // If user is typing (internalInputValue has value), always show that
    if (internalInputValue) return internalInputValue;
    // When dropdown is open and no user has typed anything, return empty string so BaseAutocomplete shows all options
    // (BaseAutocomplete filters options based on input value, so we need to clear it to show all)
    // This fixes the issue where only the currently selected option is visible in the dropdown
    if (effectiveOpen) return '';
    // Otherwise, show selected value's label if there is one
    if (internalSelectedValue === null) return '';
    const single = internalSelectedValue as SelectOption<OptionValueType> | string | null;
    if (single === null) return '';
    if (typeof single === 'string') return single;
    return getItemStringValue(single);
  }, [multiple, internalSelectedValue, internalInputValue, getItemStringValue, isClearingInput, effectiveOpen]);

  // ===== EVENT HANDLERS =====
  const handleOpenInternal = useCallback(() => {
    if (enableFetchOnScroll) setupScrollListener();
    // Clear input value when opening to show all options (BaseAutocomplete filters based on input value)
    if (!internalInputValue) {
      setInternalInputValue('');
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
    handleOpenFromHook();
  }, [enableFetchOnScroll, setupScrollListener, handleOpenFromHook, internalInputValue, inputRef]);

  const handleReset = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      // Clear selected value(s)
      onChange?.({ target: { value: (multiple ? '[]' : '') as Jsonified<any> } });

      // Clear search text in both local state and upstream hook
      setIsClearingInput(true);
      setInternalInputValue('');
      onSearch?.('');
      handleInputChangeFromHook({ target: { value: '' } } as any, '', 'clear');

      if (inputRef.current) {
        inputRef.current.value = '';
        // Do not focus the input here, otherwise the focus handler may
        // immediately reopen the dropdown even when we want it to stay closed.
      }

      // Force remount of BaseAutocomplete to drop any internal state
      setResetKey(prev => prev + 1);

      // Allow displayValue to recompute after React processes updates
      setTimeout(() => {
        setIsClearingInput(false);
      }, 0);
    },
    [multiple, onChange, onSearch, handleInputChangeFromHook, inputRef],
  );

  const handleArrowClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      suppressOpenRef.current = false;

      if (isControlledOpen) {
        if (effectiveOpen) {
          onClose?.();
        } else {
          onOpen?.();
        }
      } else {
        setInternalOpen(prev => !prev);
      }
    },
    [effectiveOpen, isControlledOpen, onOpen, onClose],
  );

  const handleArrowKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();

        if (isControlledOpen) {
          if (effectiveOpen) {
            onClose?.();
          } else {
            onOpen?.();
          }
        } else {
          setInternalOpen(prev => !prev);
        }
      }
    },
    [effectiveOpen, isControlledOpen, onOpen, onClose],
  );

  // ===== COMPUTED VALUES =====
  const showReset = internalSelectedValue && (multiple ? (internalSelectedValue as Array<any>).length > 1 : true);
  const hasSingleSelection = !multiple && internalSelectedValue !== null;
  const hasInlineClear = !loading && !endDecorator && (!!internalInputValue || hasSingleSelection);

  // Select All / Deselect All
  const allSelected =
    multiple &&
    enableSelectAll &&
    Array.isArray(internalSelectedValue) &&
    options.length > 0 &&
    internalSelectedValue.length === options.length;

  const handleToggleSelectAll = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      (e as React.MouseEvent & { preventBaseUIHandler?: () => void }).preventBaseUIHandler?.();
      if (allSelected) {
        handleValueChange([]);
      } else {
        handleValueChange(options as Array<SelectOption<OptionValueType>>);
      }
    },
    [allSelected, handleValueChange, options],
  );

  // ===== RENDER =====
  return (
    <div
      ref={rootRef}
      onClick={handleRootClick}
      style={{
        width: '100%',
        position: 'relative',
      }}
    >
      <BaseAutocomplete.Root
        key={resetKey}
        items={options as any}
        value={displayValue || undefined}
        onValueChange={handleBaseInputChange}
        open={effectiveOpen}
        onOpenChange={(isOpen: boolean) => {
          // After a close-on-selection, suppress any BaseAutocomplete attempts
          // to reopen (e.g. because the input is still focused).
          if (isOpen && suppressOpenRef.current) {
            return;
          }

          if (!isControlledOpen) {
            setInternalOpen(isOpen);
          }

          if (isOpen) {
            handleOpenInternal();
          } else {
            onClose?.();
          }
        }}
        itemToStringValue={getItemStringValue as any}
        disabled={disabled}
        mode="list"
      >
        {/* Input Area */}
        <div
          ref={inputContainerRef}
          data-autocomplete-input-container
          style={{
            position: 'relative',
            width: '100%',
            backgroundColor: 'var(--joy-palette-grey1)',
            borderRadius: '2px',
            border: `1px solid ${restingBorderColor}`,
            transition: 'border-color 0.2s',
            ...inputContainerStyle,
          }}
          onMouseEnter={e => {
            if (!disabled && !error) {
              e.currentTarget.style.borderColor = 'var(--joy-palette-grey2)';
            }
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = restingBorderColor;
          }}
        >
          {startDecorator && <div>{startDecorator}</div>}
          <div style={{ position: 'relative', width: '100%' }}>
            <BaseAutocomplete.Input
              ref={inputRef}
              placeholder={
                effectiveOpen && !internalInputValue && !multiple && internalSelectedValue !== null ? '' : placeholder
              }
              onFocus={handleFocus}
              onBlur={handleInputBlur}
              onKeyDown={e => {
                if (e.key === 'Backspace') {
                  // Handle backspace when there's only one character left
                  // This prevents the clear button from disappearing and the character from reappearing
                  // Force remount of BaseAutocomplete to clear its internal state, just like the clear button does
                  if (internalInputValue && internalInputValue.length === 1) {
                    // Use a microtask to ensure we update after BaseAutocomplete processes the backspace
                    // but before it tries to restore any value
                    setTimeout(() => {
                      setIsClearingInput(true);
                      setInternalInputValue('');
                      onSearch?.('');
                      handleInputChangeFromHook({ target: { value: '' } } as any, '', 'clear');
                      if (inputRef.current) {
                        inputRef.current.value = '';
                      }
                      // Force remount of BaseAutocomplete to ensure internal state is wiped
                      setResetKey(prev => prev + 1);
                      // Reset clearing flag after a brief delay to allow React to update
                      setTimeout(() => {
                        setIsClearingInput(false);
                      }, 0);
                    }, 0);
                    // Don't prevent default - let BaseAutocomplete handle the backspace naturally
                    // but we'll override the value immediately after
                  } else if (!multiple && !internalInputValue && internalSelectedValue !== null) {
                    // Allow clearing the selected value with Backspace when the field looks "filled"
                    handleValueChange(null);
                  }
                }
              }}
              autoComplete="off"
              style={{
                width: '100%',
                height: '36px',
                paddingLeft: '12px',
                // Reserve space for clear + chevron icons on the right,
                // matching the spacing of the regular Select component.
                paddingRight: hasInlineClear ? '52px' : '36px',
                backgroundColor: 'transparent',
                border: 'none',
                outline: 'none',
                fontFamily: 'var(--joy-fontFamily-display)',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '18px',
                color: 'var(--joy-palette-black)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              className="autocomplete-input-field"
            />
            {/* Overlay to show selected value when dropdown is open and input is empty for filtering */}
            {effectiveOpen && !internalInputValue && !multiple && internalSelectedValue !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  fontFamily: 'var(--joy-fontFamily-display)',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  color: 'var(--joy-palette-black)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 'calc(100% - 48px)',
                }}
              >
                {(() => {
                  const single = internalSelectedValue as SelectOption<OptionValueType> | string | null;
                  if (single === null) return '';
                  if (typeof single === 'string') return single;
                  return getItemStringValue(single);
                })()}
              </div>
            )}
          </div>
          <style>{`
            .autocomplete-input-field::placeholder {
              color: var(--joy-palette-grey4) !important;
              opacity: 1 !important;
            }
          `}</style>
          {/* Clear search/value button */}
          {hasInlineClear && (
            <div
              role="button"
              tabIndex={0}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                // If we are in single-select mode with a selected value and no active search text,
                // treat this as "clear the selection".
                if (!multiple && internalSelectedValue !== null && !internalInputValue) {
                  handleValueChange(null);
                }
                // Forcefully clear all input/search state
                setIsClearingInput(true);
                setInternalInputValue('');
                onSearch?.('');
                handleInputChangeFromHook({ target: { value: '' } } as any, '', 'clear');

                if (inputRef.current) {
                  inputRef.current.value = '';
                }

                // Force remount of BaseAutocomplete to ensure internal state is wiped
                setResetKey(prev => prev + 1);

                // Reset clearing flag after a brief delay to allow React to update
                setTimeout(() => {
                  setIsClearingInput(false);
                }, 0);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  // Forcefully clear all input/search state
                  setIsClearingInput(true);
                  setInternalInputValue('');
                  onSearch?.('');
                  handleInputChangeFromHook({ target: { value: '' } } as any, '', 'clear');

                  if (inputRef.current) {
                    inputRef.current.value = '';
                    inputRef.current.focus();
                  }

                  // Force remount of BaseAutocomplete to ensure internal state is wiped
                  setResetKey(prev => prev + 1);

                  // Reset clearing flag after a brief delay to allow React to update
                  setTimeout(() => {
                    setIsClearingInput(false);
                  }, 0);
                }
              }}
              style={{
                position: 'absolute',
                // Slightly closer to the chevron to visually align with Select
                right: '32px',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                pointerEvents: 'auto',
              }}
            >
              <Icon name={IconName.close} size={16} color="var(--joy-palette-grey4)" />
            </div>
          )}
          {/* Arrow button - absolute positioned on the right */}
          {!loading &&
            (endDecorator || (
              <div
                role="button"
                tabIndex={0}
                onClick={handleArrowClick}
                onKeyDown={handleArrowKeyDown}
                style={{
                  position: 'absolute',
                  // Match Select's chevron alignment
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  pointerEvents: 'auto',
                }}
              >
                {endDecorator || (
                  <Icon name={IconName.chevronDown} size={22} color={iconColor || 'var(--joy-palette-grey4)'} />
                )}
              </div>
            ))}
          {loading && (
            <div
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'auto',
              }}
            >
              <Spinner size="sm" />
            </div>
          )}
        </div>

        {/* Selected Options Tags */}
        {multiple && Array.isArray(internalSelectedValue) && internalSelectedValue.length > 0 && (
          <div
            style={{
              marginTop: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '6px',
            }}
          >
            {/* Tags row: wrap as needed */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
              }}
            >
              {internalSelectedValue.map((value, index) => (
                <Tag
                  key={typeof value === 'string' ? value : (value.value as Key)}
                  label={typeof value === 'string' ? value : (value.labelString ?? '')}
                  endDecorator={<Icon name={IconName.close} size={16} />}
                  onIconClick={() => {
                    const newArray = [...internalSelectedValue];
                    newArray.splice(index, 1);
                    handleValueChange(newArray);
                  }}
                  onTagClick={() => {
                    const newArray = [...internalSelectedValue];
                    newArray.splice(index, 1);
                    handleValueChange(newArray);
                  }}
                />
              ))}
            </div>
            {/* Clear button always below the tags */}
            {showReset && !disabled && (
              <ClearSelectionButton type="button" onClick={handleReset}>
                <span
                  style={{
                    fontFamily: 'var(--joy-fontFamily-display)',
                    fontSize: '11px',
                    lineHeight: '16px',
                    letterSpacing: '0.08em',
                    marginLeft: '8px',
                    fontWeight: 500,
                  }}
                >
                  {t('label.clearSelection').toUpperCase()}
                </span>
              </ClearSelectionButton>
            )}
          </div>
        )}

        {/* Dropdown */}
        <BaseAutocomplete.Portal>
          <BaseAutocomplete.Positioner
            sideOffset={4}
            align="start"
            style={{
              // Ensure the dropdown appears above all other UI (tables, modals, etc.)
              zIndex: 99999,
            }}
          >
            <BaseAutocomplete.Popup
              style={{
                maxHeight: '300px',
                overflowY: 'auto',
                overflowX: 'hidden',
                backgroundColor: 'white',
                // Keep a very high z-index for the popup itself as well
                zIndex: 99999,
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.12)',
                width: `${dropdownWidth}px`,
                minWidth: '300px',
              }}
            >
              <BaseAutocomplete.Empty>
                <div
                  style={{
                    padding: '12px',
                    color: 'var(--joy-palette-grey4)',
                    textAlign: 'center',
                    fontFamily: 'var(--joy-fontFamily-display)',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '18px',
                  }}
                >
                  {t('label.noOptionFound')}
                </div>
              </BaseAutocomplete.Empty>
              {multiple && enableSelectAll && options.length > 0 && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={handleToggleSelectAll}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleToggleSelectAll(e as unknown as React.MouseEvent);
                    }
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = 'var(--joy-palette-grey1)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    height: '42px',
                    gap: '6px',
                    paddingLeft: '12px',
                    paddingRight: '12px',
                    cursor: 'pointer',
                    fontFamily: 'var(--joy-fontFamily-display)',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '18px',
                    color: 'var(--joy-palette-black)',
                    borderBottom: '1px solid var(--joy-palette-grey2)',
                  }}
                >
                  {allSelected ? (
                    <Icon key="selected" name={IconName.checkBox} size={16} color="var(--joy-palette-bluePrimary)" />
                  ) : (
                    <Icon
                      key="unselected"
                      name={IconName.checkBoxOutlineBlank}
                      size={16}
                      color="var(--joy-palette-grey4)"
                    />
                  )}
                  {allSelected ? t('label.deselectAll') : t('label.selectAll')}
                </div>
              )}
              <BaseAutocomplete.List
                ref={listboxRef as React.Ref<HTMLDivElement>}
                onScroll={onListboxScroll as ((event: React.UIEvent<HTMLDivElement>) => void) | undefined}
                style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  width: `${dropdownWidth}px`,
                  minWidth: '300px',
                }}
              >
                {(item: SelectOption<OptionValueType> | string) => {
                  const isSelected = multiple
                    ? Array.isArray(internalSelectedValue) &&
                      internalSelectedValue.some(v =>
                        typeof item === 'string'
                          ? v === item
                          : typeof v === 'string'
                            ? false
                            : _.isEqual(
                                (v as SelectOption<OptionValueType>).value,
                                (item as SelectOption<OptionValueType>).value,
                              ),
                      )
                    : (() => {
                        const single = internalSelectedValue as SelectOption<OptionValueType> | string | null;
                        if (single === null) return false;
                        if (typeof item === 'string') return single === item;
                        if (typeof single === 'string') return false;
                        return _.isEqual(single.value, (item as SelectOption<OptionValueType>).value);
                      })();

                  const handleItemClick = (e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Prevent Base UI's internal commitSelection from running after our handler.
                    // Without this, Base UI fills the input with the item's label text (via onValueChange),
                    // which interferes with our search clearing and triggers cascading state issues.
                    (e as React.MouseEvent & { preventBaseUIHandler?: () => void }).preventBaseUIHandler?.();
                    if (multiple) {
                      const current = (internalSelectedValue as Array<SelectOption<OptionValueType> | string>) ?? [];
                      const exists = current.some(v =>
                        typeof item === 'string'
                          ? v === item
                          : typeof v === 'string'
                            ? false
                            : _.isEqual(
                                (v as SelectOption<OptionValueType>).value,
                                (item as SelectOption<OptionValueType>).value,
                              ),
                      );
                      handleValueChange(
                        exists
                          ? current.filter(v =>
                              typeof item === 'string'
                                ? v !== item
                                : typeof v === 'string'
                                  ? false
                                  : !_.isEqual(
                                      (v as SelectOption<OptionValueType>).value,
                                      (item as SelectOption<OptionValueType>).value,
                                    ),
                            )
                          : [...current, item],
                      );
                      // After any selection in multi mode, clear the search field.
                      // Use a microtask to ensure we run AFTER BaseAutocomplete's own onValueChange.
                      if (closeOnSelection) {
                        suppressOpenRef.current = true;
                      }
                      setTimeout(() => {
                        setInternalInputValue('');
                        onSearch?.('');
                        // Only call handleInputChangeFromHook when keeping the dropdown open.
                        // It schedules a refocus that would trigger autoOpenOnFocus and reopen
                        // the dropdown after we close it.
                        if (!closeOnSelection) {
                          handleInputChangeFromHook({ target: { value: '' } } as any, '', 'reset');
                        }
                        if (inputRef.current) {
                          inputRef.current.value = '';
                        }
                        // Force BaseAutocomplete to remount so any internal input
                        // state is fully reset (same strategy as the clear icon).
                        setResetKey(prev => prev + 1);

                        // Close dropdown after selection if closeOnSelection is true
                        if (closeOnSelection) {
                          if (isControlledOpen) {
                            onClose?.();
                          } else {
                            setInternalOpen(false);
                          }
                          // Blur input to prevent autoOpenOnFocus from reopening
                          inputRef.current?.blur();
                          // Clear suppress flag after a tick so future user interactions work
                          setTimeout(() => {
                            suppressOpenRef.current = false;
                          }, 0);
                        }
                      }, 0);
                    } else {
                      handleValueChange(item);
                      // Close the dropdown after single selection.
                      if (closeOnSelection) {
                        suppressOpenRef.current = true;
                        if (isControlledOpen) {
                          onClose?.();
                        } else {
                          setInternalOpen(false);
                        }
                      }
                      setTimeout(() => {
                        setInternalInputValue('');
                        onSearch?.('');
                        // Only call handleInputChangeFromHook when keeping the dropdown open.
                        // It schedules a refocus that would trigger autoOpenOnFocus and reopen
                        // the dropdown after we close it.
                        if (!closeOnSelection) {
                          handleInputChangeFromHook({ target: { value: '' } } as any, '', 'reset');
                        }
                        if (inputRef.current) {
                          inputRef.current.value = '';
                        }
                        setResetKey(prev => prev + 1);
                        if (closeOnSelection) {
                          // Blur input to prevent autoOpenOnFocus from reopening
                          inputRef.current?.blur();
                          // Clear suppress flag after a tick so future user interactions work
                          setTimeout(() => {
                            suppressOpenRef.current = false;
                          }, 0);
                        }
                      }, 0);
                    }
                  };

                  return (
                    <BaseAutocomplete.Item
                      key={typeof item === 'string' ? item : ((item as SelectOption<OptionValueType>).value as Key)}
                      value={item}
                      onClick={handleItemClick}
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        height: '42px',
                        gap: '6px',
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        cursor: 'pointer',
                        fontFamily: 'var(--joy-fontFamily-display)',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '18px',
                        color: 'var(--joy-palette-black)',
                        borderBottom: '1px solid var(--joy-palette-grey1)',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = 'var(--joy-palette-grey1)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {multiple && (
                        <>
                          {isSelected ? (
                            <Icon
                              key="selected"
                              name={IconName.checkBox}
                              size={16}
                              color="var(--joy-palette-bluePrimary)"
                            />
                          ) : (
                            <Icon
                              key="unselected"
                              name={IconName.checkBoxOutlineBlank}
                              size={16}
                              color="var(--joy-palette-grey4)"
                            />
                          )}
                        </>
                      )}
                      {typeof item === 'string' ? item : item.label}
                    </BaseAutocomplete.Item>
                  );
                }}
              </BaseAutocomplete.List>
            </BaseAutocomplete.Popup>
          </BaseAutocomplete.Positioner>
        </BaseAutocomplete.Portal>
      </BaseAutocomplete.Root>
    </div>
  );
};

const Autocomplete =
  (multiple: boolean, allowCustomValue: boolean) =>
  <OptionValueType,>(props: AutocompleteProps<OptionValueType, typeof multiple, typeof allowCustomValue>) => (
    <AutocompleteComponent<OptionValueType> {...props} multiple={multiple} allowCustomValue={allowCustomValue} />
  );

export default Autocomplete;
